/**
 * Comments — spec §5-F7, V3 R3.
 *
 * Threaded one level (reply to a comment, never to a reply), emoji reactions,
 * a 24h edit window, and tombstoned deletes that keep thread order.
 */

import { useState } from 'react';
import {
  CornerDownRight,
  Loader2,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  SmilePlus,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { UserLink } from '@/components/shared/UserLink';
import { EmptyState } from '@/components/shared/EmptyState';
import { RichTextEditor } from '@/components/shared/RichTextEditor';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/features/auth';
import {
  useComments,
  useCreateComment,
  useDeleteComment,
  useReact,
  useUpdateComment,
  type CommentView,
} from './api';

const REACTIONS = ['👍', '🎉', '👀', '🙏', '🚀'];
const EDIT_WINDOW_MS = 24 * 60 * 60 * 1000;

function relative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleDateString();
}

function Comment({
  comment,
  taskId,
  replies,
  onReply,
}: {
  comment: CommentView;
  taskId: string;
  replies: CommentView[];
  onReply: (id: string) => void;
}) {
  const { user } = useAuth();
  const update = useUpdateComment(taskId);
  const remove = useDeleteComment(taskId);
  const react = useReact(taskId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);
  const [showReactions, setShowReactions] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const isMine = comment.authorId === user?._id;
  const withinWindow = Date.now() - new Date(comment.createdAt).getTime() < EDIT_WINDOW_MS;

  // V6-02: the tombstone stays in the thread so a reply that answered this
  // comment still reads as an answer to something. The server has already
  // scrubbed the body and the attachments — there is nothing here to hide.
  if (comment.isTombstoned || comment.isDeleted) {
    const moderated = Boolean(comment.tombstonedBy && comment.tombstonedBy !== comment.authorId);
    return (
      <li className="py-2" data-testid="comment-tombstone" data-comment-id={comment._id}>
        <p className="pl-8 text-[12px] italic vt-muted">
          {moderated
            ? `Comment deleted by ${comment.tombstonedByName ?? 'a moderator'}`
            : 'Comment deleted'}
        </p>
        {replies.length > 0 && (
          <ul className="ml-8 mt-1 border-l border-[var(--border)] pl-3">
            {replies.map((reply) => (
              <Comment key={reply._id} comment={reply} taskId={taskId} replies={[]} onReply={onReply} />
            ))}
          </ul>
        )}
      </li>
    );
  }

  return (
    <li className="py-2">
      <div className="flex gap-2">
        <UserLink userId={comment.authorId} name={comment.authorName} avatarOnly size={24} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[12px]">
            <UserLink
              userId={comment.authorId}
              name={comment.authorName}
              nameOnly
              className="font-medium"
            />
            <span className="vt-muted">{relative(comment.createdAt)}</span>
            {comment.editedAt && <span className="vt-muted">· edited</span>}
          </div>

          {editing ? (
            <div className="mt-1 flex flex-col gap-1.5">
              <RichTextEditor
                value={comment.body}
                onChange={setDraft}
                minHeight={60}
                onSubmit={() => void save()}
              />
              <div className="flex gap-1.5">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => void save()}
                  disabled={update.isPending}
                >
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="vt-prose mt-0.5">
              <RichTextEditor value={comment.body} editable={false} toolbar={false} minHeight={0} />
            </div>
          )}

          <div className="mt-1 flex flex-wrap items-center gap-1">
            {(comment.reactions ?? []).map((r) => (
              <button
                key={r.emoji}
                type="button"
                onClick={() =>
                  react.mutate({
                    id: comment._id,
                    emoji: r.emoji,
                    on: !r.userIds.includes(user?._id ?? ''),
                  })
                }
                className={`rounded-full border px-1.5 py-0.5 text-[11px] transition-colors duration-[120ms] ${
                  r.userIds.includes(user?._id ?? '')
                    ? 'border-[var(--accent)] bg-[var(--accent-soft)]'
                    : 'border-[var(--border)] hover:bg-[var(--surface-2)]'
                }`}
              >
                {r.emoji} {r.userIds.length}
              </button>
            ))}

            <div className="relative">
              <button
                type="button"
                aria-label="Add a reaction"
                onClick={() => setShowReactions((v) => !v)}
                className="grid size-6 place-items-center rounded-[4px] vt-muted hover:bg-[var(--surface-2)]"
              >
                <SmilePlus className="size-3.5" />
              </button>
              {showReactions && (
                <div className="vt-surface vt-elevated absolute left-0 top-7 z-10 flex gap-0.5 p-1">
                  {REACTIONS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => {
                        react.mutate({ id: comment._id, emoji, on: true });
                        setShowReactions(false);
                      }}
                      className="rounded-[4px] px-1 text-[14px] hover:bg-[var(--surface-2)]"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {!comment.parentCommentId && (
              <button
                type="button"
                onClick={() => onReply(comment._id)}
                className="rounded-[4px] px-1.5 py-0.5 text-[11px] vt-muted hover:bg-[var(--surface-2)]"
              >
                Reply
              </button>
            )}

            {isMine && withinWindow && (
              <button
                type="button"
                aria-label="Edit comment"
                onClick={() => {
                  setDraft(comment.body);
                  setEditing(true);
                }}
                className="grid size-6 place-items-center rounded-[4px] vt-muted hover:bg-[var(--surface-2)]"
              >
                <Pencil className="size-3" />
              </button>
            )}
            {comment.canDelete && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="Comment actions"
                    data-testid="comment-menu"
                    className="grid size-6 place-items-center rounded-[4px] vt-muted hover:bg-[var(--surface-2)]"
                  >
                    <MoreHorizontal className="size-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    destructive
                    data-testid="comment-delete"
                    onSelect={() => setConfirming(true)}
                  >
                    <Trash2 className="size-3.5" />
                    Delete comment
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Inline rather than a modal: the thread is the context, and a
              dialog that hides it makes "which one?" a guess. */}
          {confirming && (
            <div
              data-testid="comment-delete-confirm"
              className="mt-1.5 flex flex-wrap items-center gap-2 rounded-[6px] border border-[var(--border)] bg-[var(--surface-2)] px-2 py-1.5 text-[12px]"
            >
              <span>Delete this comment? This can&apos;t be undone.</span>
              <Button
                size="sm"
                variant="danger"
                data-testid="comment-delete-confirm-yes"
                disabled={remove.isPending}
                onClick={() => void confirmDelete()}
              >
                {remove.isPending ? <Loader2 className="animate-spin" /> : null}
                Delete
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </div>
          )}

          {replies.length > 0 && (
            <ul className="mt-1 border-l border-[var(--border)] pl-3">
              {replies.map((reply) => (
                <Comment key={reply._id} comment={reply} taskId={taskId} replies={[]} onReply={onReply} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </li>
  );

  async function confirmDelete(): Promise<void> {
    try {
      await remove.mutateAsync(comment._id);
      setConfirming(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete the comment');
    }
  }

  async function save(): Promise<void> {
    if (!draft) {
      setEditing(false);
      return;
    }
    try {
      await update.mutateAsync({ id: comment._id, body: draft });
      setEditing(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save the edit');
    }
  }
}

export function CommentThread({ taskId }: { taskId: string }) {
  const { data: comments = [], isLoading } = useComments('task', taskId);
  const create = useCreateComment(taskId);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [body, setBody] = useState<Record<string, unknown> | null>(null);
  const [nonce, setNonce] = useState(0);

  const roots = comments.filter((c) => !c.parentCommentId);
  const repliesOf = (id: string): CommentView[] => comments.filter((c) => c.parentCommentId === id);

  async function post(): Promise<void> {
    const content = ((body?.content as unknown[]) ?? []).length > 0;
    if (!content) return;
    try {
      await create.mutateAsync({
        entityType: 'task',
        entityId: taskId,
        body: body!,
        ...(replyTo ? { parentCommentId: replyTo } : {}),
      });
      setBody(null);
      setReplyTo(null);
      // Remounts the editor so it clears — TipTap keeps its own document.
      setNonce((n) => n + 1);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not post the comment');
    }
  }

  return (
    <section data-testid="comment-thread">
      <h3 className="mb-2 text-[13px] font-medium">
        Comments {comments.length > 0 && <span className="vt-muted">({comments.length})</span>}
      </h3>

      {isLoading ? null : roots.length === 0 ? (
        <EmptyState
          icon={<MessageSquare className="size-5" />}
          title="No comments"
          description="Decisions made here stay findable. @mention someone to pull them in."
        />
      ) : (
        <ul className="divide-y divide-[var(--border)]">
          {roots.map((comment) => (
            <Comment
              key={comment._id}
              comment={comment}
              taskId={taskId}
              replies={repliesOf(comment._id)}
              onReply={setReplyTo}
            />
          ))}
        </ul>
      )}

      <div className="mt-3">
        {replyTo && (
          <div className="mb-1 flex items-center gap-1 text-[12px] vt-muted">
            <CornerDownRight className="size-3.5" />
            Replying
            <button
              type="button"
              onClick={() => setReplyTo(null)}
              className="underline hover:text-[var(--text)]"
            >
              cancel
            </button>
          </div>
        )}
        <RichTextEditor
          key={nonce}
          data-testid="comment-editor"
          minHeight={60}
          toolbar={false}
          placeholder="Write a comment — ⌘Enter to post"
          onChange={setBody}
          onSubmit={() => void post()}
        />
        <div className="mt-1.5 flex justify-end">
          <Button
            size="sm"
            variant="primary"
            data-testid="post-comment"
            disabled={create.isPending}
            onClick={() => void post()}
          >
            {create.isPending ? <Loader2 className="animate-spin" /> : null}
            Comment
          </Button>
        </div>
      </div>
    </section>
  );
}
